/**
 * Adapted from Open Science `apps/desktop/src/components/thread/Composer.tsx`
 * at e2333ef9bdefb9c5438c54fc78d93fe56ec72ed0 (MIT).
 *
 * Cadence keeps Open Science's direct keyboard palette and reference chips,
 * while its Project/Artifact/Execution contracts remain the source of truth.
 */
import {
  ArrowUp,
  FileText,
  LoaderCircle,
  Paperclip,
  Square,
  Upload,
  X,
} from "lucide-react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ClipboardEvent,
  type DragEvent,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from "react";
import type {
  CadenceAgent,
  CadenceArtifact,
  CadenceAttachment,
  CadenceComposerCapability,
  CadenceComposerIntent,
  CadenceComposerReference,
  CadenceTurn,
} from "./types";
import {
  controlledCommandInput,
  withoutControlledCommandMarker,
} from "./composerSyntax";

type ReferenceTrigger = {
  kind: "at" | "history";
  query: string;
  start: number;
};

type ReferenceItem =
  | { kind: "agent"; id: string; label: string; detail: string }
  | { kind: "attachment"; id: string; label: string; detail: string }
  | { kind: "artifact"; id: string; label: string; detail: string }
  | { kind: "project_turn"; id: string; label: string; detail: string };

function referenceTriggerAt(value: string, caret: number): ReferenceTrigger | undefined {
  const prefix = value.slice(0, caret);
  const match = /(^|\s)([@#])([^\s@#]*)$/u.exec(prefix);
  if (!match) return undefined;
  return {
    kind: match[2] === "@" ? "at" : "history",
    query: match[3].toLocaleLowerCase(),
    start: caret - match[2].length - match[3].length,
  };
}

function replaceReference(
  value: string,
  trigger: ReferenceTrigger,
  caret: number,
  replacement: string,
) {
  return {
    value: `${value.slice(0, trigger.start)}${replacement}${value.slice(caret)}`,
    caret: trigger.start + replacement.length,
  };
}

function matchesReference(item: ReferenceItem, query: string): boolean {
  if (!query) return true;
  return `${item.label} ${item.detail}`.toLocaleLowerCase().includes(query);
}

function turnLabel(turn: CadenceTurn): string {
  const input = turn.input.trim() || "基于附件的研究请求";
  return input.length > 64 ? `${input.slice(0, 64)}…` : input;
}

/**
 * Project-native input layer. `@` keeps Cadence's existing Agent addressing
 * alongside durable Project Input/Artifact chips; `#` carries stable
 * ProjectTurn references. Neither picker starts a turn by itself.
 */
export function OpenScienceComposer({
  value,
  sending,
  running = false,
  steerAvailable = false,
  stopping = false,
  onStop,
  disabled,
  disabledReason,
  attachments,
  availableAttachments = [],
  artifacts = [],
  agents = [],
  turns = [],
  references = [],
  capabilities = [],
  intent,
  uploading,
  sendError,
  uploadError,
  context,
  interaction,
  auxiliaryContent,
  inputRef,
  onChange,
  onSubmit,
  onUploadFiles,
  onAttachProjectInput,
  onAddReference,
  onRemoveReference,
  onIntentChange,
  onRemoveAttachment,
  onOpenAttachment,
  onRetryUpload,
  onFocusChange,
  pinned,
  compact,
  allowAttachmentOnly = true,
}: {
  value: string;
  sending: boolean;
  /** A server-confirmed ProjectTurn is active and can be stopped. */
  running?: boolean;
  /** The active Organization root has a server-confirmed Planner Lead. */
  steerAvailable?: boolean;
  /** Stop request is in flight; do not issue a second cancellation. */
  stopping?: boolean;
  onStop?(): void;
  disabled: boolean;
  disabledReason?: string;
  attachments: CadenceAttachment[];
  availableAttachments?: CadenceAttachment[];
  artifacts?: CadenceArtifact[];
  agents?: CadenceAgent[];
  turns?: CadenceTurn[];
  references?: CadenceComposerReference[];
  capabilities?: CadenceComposerCapability[];
  intent: CadenceComposerIntent;
  uploading: boolean;
  sendError?: string;
  uploadError?: string;
  context: ReactNode;
  interaction?: ReactNode;
  auxiliaryContent?: ReactNode;
  inputRef: RefObject<HTMLTextAreaElement>;
  onChange(value: string): void;
  onSubmit(): Promise<void> | void;
  onUploadFiles?(files: File[]): void;
  onAttachProjectInput?(attachmentId: string): void;
  onAddReference?(reference: CadenceComposerReference): void;
  onRemoveReference?(referenceId: string): void;
  onIntentChange?(intent: CadenceComposerIntent): void;
  onRemoveAttachment?(attachmentId: string): void;
  onOpenAttachment?(attachment: CadenceAttachment): void;
  onRetryUpload?: () => void;
  onFocusChange?(focused: boolean): void;
  pinned?: boolean;
  compact?: boolean;
  allowAttachmentOnly?: boolean;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const composerRef = useRef<HTMLElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [caret, setCaret] = useState(value.length);
  const [paletteIndex, setPaletteIndex] = useState(0);
  const [paletteDismissed, setPaletteDismissed] = useState(false);
  const controlledCapability = capabilities.find(
    (capability) => capability.kind === "controlled_command",
  );
  const controlledCommand = controlledCommandInput(value);
  const controlledCommandTyped = Boolean(controlledCommand);
  const controlledCommandReady = Boolean(controlledCommand?.command.trim());
  const controlledCommandUnavailable =
    controlledCommandTyped && !controlledCapability?.available;
  const controlledCommandNeedsAgent =
    controlledCommandTyped &&
    controlledCapability?.requiresAgentSelection === true &&
    !controlledCommand?.agentId;
  const controlledCommandUnknownAgent =
    controlledCommandTyped &&
    Boolean(controlledCommand?.agentId) &&
    !agents.some((agent) => agent.id === controlledCommand?.agentId);
  const steerEligible = Boolean(
    running &&
      steerAvailable &&
      value.trim() &&
      attachments.length === 0 &&
      references.length === 0 &&
      !controlledCommandTyped &&
      intent.kind === "project_investigation",
  );
  // PASEO keeps Stop for an active empty/unsupported draft, but turns it back
  // into Send for the one server-authorized steering path. The Composer must
  // not make a running task look ready for an unrelated new ProjectTurn.
  const canStop = Boolean(running && onStop && !steerEligible);
  const slashMatch = /^\/([^\s/]*)$/u.exec(value.slice(0, caret));
  const capabilityItems = useMemo(() => {
    const query = slashMatch?.[1].toLocaleLowerCase() ?? "";
    return slashMatch
      ? capabilities.filter((capability) =>
          `${capability.id} ${capability.label} ${capability.description}`
            .toLocaleLowerCase()
            .includes(query),
        )
      : [];
  }, [capabilities, slashMatch]);
  const activeReferenceTrigger = useMemo(
    () => referenceTriggerAt(value, caret),
    [caret, value],
  );
  const referenceItems = useMemo<ReferenceItem[]>(() => {
    if (!activeReferenceTrigger) return [];
    if (activeReferenceTrigger.kind === "history") {
      return turns.flatMap((turn) =>
        turn.requestId && !turn.optimistic
          ? [{
              kind: "project_turn" as const,
              id: turn.requestId,
              label: turnLabel(turn),
              detail: `历史请求 · ${turn.status}`,
            }]
          : [],
      ).filter((item) => matchesReference(item, activeReferenceTrigger.query));
    }
    const agentItems = agents.map((agent) => ({
      kind: "agent" as const,
      id: agent.id,
      label: agent.name,
      detail: `Agent · @${agent.id}`,
    }));
    const attachmentItems = availableAttachments.map((attachment) => ({
      kind: "attachment" as const,
      id: attachment.id,
      label: attachment.name,
      detail: "项目文件 · 添加至本次请求",
    }));
    const artifactItems = artifacts.map((artifact) => ({
      kind: "artifact" as const,
      id: artifact.id,
      label: artifact.name,
      detail: `Artifact · ${artifact.type}`,
    }));
    return [...agentItems, ...attachmentItems, ...artifactItems].filter((item) =>
      matchesReference(item, activeReferenceTrigger.query),
    );
  }, [activeReferenceTrigger, agents, artifacts, availableAttachments, turns]);
  const paletteKind = capabilityItems.length
    ? "capabilities"
    : referenceItems.length
      ? "references"
      : undefined;
  const paletteItems =
    paletteKind === "capabilities" ? capabilityItems : referenceItems;
  const paletteOpen = Boolean(paletteKind && !paletteDismissed && !disabled);
  const activeIndex = Math.min(
    paletteIndex,
    Math.max(0, paletteItems.length - 1),
  );
  // A controlled command cannot be treated as attachment-only research input.
  const canSubmit =
    !disabled &&
    !sending &&
    !uploading &&
    !controlledCommandUnavailable &&
    !controlledCommandNeedsAgent &&
    !controlledCommandUnknownAgent &&
    (!running
      ? (controlledCommandTyped
        ? controlledCommandReady
        : Boolean(value.trim() || (allowAttachmentOnly && attachments.length)))
      : steerEligible);
  const acceptFiles = (files: File[]) => {
    if (disabled || running || uploading || !files.length) return;
    onUploadFiles?.(files);
  };
  const focusAt = (nextCaret: number) => {
    window.requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(nextCaret, nextCaret);
    });
  };
  const closePalette = () => setPaletteDismissed(true);
  const chooseCapability = (capability: CadenceComposerCapability) => {
    if (!capability.available) return;
    onIntentChange?.(
      capability.kind === "controlled_command"
        ? { kind: "controlled_command", command: "" }
        : { kind: capability.kind },
    );
    const nextValue = capability.kind === "controlled_command" ? "!" : "";
    onChange(nextValue);
    setCaret(nextValue.length);
    closePalette();
    focusAt(nextValue.length);
  };
  const chooseReference = (item: ReferenceItem) => {
    if (!activeReferenceTrigger) return;
    if (item.kind === "agent") {
      const next = replaceReference(value, activeReferenceTrigger, caret, `@${item.id} `);
      onChange(next.value);
      setCaret(next.caret);
      closePalette();
      focusAt(next.caret);
      return;
    }
    const next = replaceReference(value, activeReferenceTrigger, caret, "");
    if (item.kind === "attachment") {
      onAttachProjectInput?.(item.id);
    } else if (item.kind === "artifact") {
      onAddReference?.({
        id: `artifact:${item.id}`,
        kind: "artifact",
        artifactId: item.id,
        label: item.label,
        detail: item.detail,
      });
    } else {
      onAddReference?.({
        id: `project-turn:${item.id}`,
        kind: "project_turn",
        requestId: item.id,
        label: item.label,
        detail: item.detail,
      });
    }
    onChange(next.value);
    setCaret(next.caret);
    closePalette();
    focusAt(next.caret);
  };
  const choosePaletteItem = () => {
    const item = paletteItems[activeIndex];
    if (!item) return;
    if (paletteKind === "capabilities") chooseCapability(item as CadenceComposerCapability);
    else chooseReference(item as ReferenceItem);
  };
  const fileDrop = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    setDragOver(false);
    acceptFiles(Array.from(event.dataTransfer.files));
  };
  const filePaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
    const files = Array.from(event.clipboardData.files);
    if (!files.length) return;
    event.preventDefault();
    acceptFiles(files);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (paletteOpen) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setPaletteIndex((current) => Math.min(current + 1, paletteItems.length - 1));
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setPaletteIndex((current) => Math.max(current - 1, 0));
        return;
      }
      if (event.key === "Enter" || event.key === "Tab") {
        event.preventDefault();
        choosePaletteItem();
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        closePalette();
        return;
      }
    }
    if (
      event.key === "Enter" &&
      !event.shiftKey &&
      !event.nativeEvent.isComposing &&
      event.keyCode !== 229
    ) {
      event.preventDefault();
      if (canSubmit) void onSubmit();
    }
  };
  useEffect(() => {
    setPaletteIndex(0);
    setPaletteDismissed(false);
  }, [value, caret]);
  useEffect(() => {
    if (!paletteOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!composerRef.current?.contains(event.target as Node)) closePalette();
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [paletteOpen]);

  return (
    <section
      className={`open-science-composer ${pinned ? "composer-mobile-pinned" : ""} ${compact ? "composer-mobile-compact" : ""} ${dragOver ? "is-file-drop-target" : ""}`}
      data-testid="composer"
      ref={composerRef}
      onDragEnter={(event) => {
        if (event.dataTransfer.types.includes("Files")) {
          event.preventDefault();
          if (!disabled && !running && !uploading) setDragOver(true);
        }
      }}
      onDragOver={(event) => {
        if (event.dataTransfer.types.includes("Files")) event.preventDefault();
      }}
      onDragLeave={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setDragOver(false);
        }
      }}
      onDrop={fileDrop}
      onBlur={(event) => {
        if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) return;
        window.setTimeout(() => {
          if (!composerRef.current?.contains(document.activeElement)) onFocusChange?.(false);
        }, 0);
      }}
    >
      {interaction}
      <div className="open-science-composer-context" data-testid="composer-context">{context}</div>
      {auxiliaryContent ?? (
        <>
          {(references.length > 0 || intent.kind !== "project_investigation") && (
            <div className="open-science-composer-references" data-testid="composer-reference-chips">
              {intent.kind !== "project_investigation" && (
                <span className="open-science-composer-reference is-capability">
                  <span>
                    {intent.kind === "research_plan"
                      ? "制定研究计划"
                      : intent.kind === "controlled_command"
                        ? "受控 Runtime/Sandbox 命令"
                        : "继续研究"}
                  </span>
                  {intent.kind !== "continue_research" && (
                    <button
                      aria-label="移除当前请求方式"
                      type="button"
                      onClick={() => {
                        onIntentChange?.({ kind: "project_investigation" });
                        if (intent.kind === "controlled_command") {
                          const nextValue = withoutControlledCommandMarker(value);
                          onChange(nextValue);
                          setCaret(Math.max(0, caret - (value.length - nextValue.length)));
                        }
                      }}
                    >
                      <X size={12} aria-hidden="true" />
                    </button>
                  )}
                </span>
              )}
              {references.map((reference) => (
                <span className="open-science-composer-reference" key={reference.id}>
                  <span>{reference.kind === "artifact" ? "Artifact" : "历史请求"} · {reference.label}</span>
                  <button
                    aria-label={`移除引用 ${reference.label}`}
                    type="button"
                    onClick={() => onRemoveReference?.(reference.id)}
                  >
                    <X size={12} aria-hidden="true" />
                  </button>
                </span>
              ))}
            </div>
          )}
          {attachments.length > 0 && (
            <div className="open-science-composer-attachments">
              {attachments.map((attachment) => {
                const status = attachmentStatusCopy(attachment.inspection);
                const visualObservationStatus = visualObservationStatusCopy(attachment.inspection);
                const documentSelectionStatus = attachment.documentSelection
                  ? documentSelectionStatusCopy(attachment.documentSelection)
                  : undefined;
                const documentViewAvailable =
                  attachment.kind === "upload" &&
                  attachment.inspection?.status === "derived_available" &&
                  attachment.inspection.parser?.name === "docling-serve";
                return (
                  <span
                    data-testid="cadence-attachment"
                    key={attachment.id}
                    title={status ? `${attachment.name}：${status}` : attachment.name}
                  >
                    <FileText size={14} aria-hidden="true" />
                    <span className="open-science-composer-attachment-copy">
                      <span className="open-science-attachment-name">{attachment.name}</span>
                      {status && <span className="open-science-attachment-status">{status}</span>}
                      {visualObservationStatus && (
                        <span className="open-science-attachment-status" data-testid="attachment-visual-observation-status">
                          {visualObservationStatus}
                        </span>
                      )}
                      {documentSelectionStatus && (
                        <span className="open-science-attachment-status" data-testid="docling-selection-context">
                          {documentSelectionStatus}
                        </span>
                      )}
                    </span>
                    {documentViewAvailable && (
                      <button
                        data-testid="open-docling-document"
                        type="button"
                        aria-label={`打开结构化文档 ${attachment.name}`}
                        onClick={() => onOpenAttachment?.(attachment)}
                      >
                        查看
                      </button>
                    )}
                    <button type="button" aria-label={`从本次请求移除附件 ${attachment.name}`} onClick={() => onRemoveAttachment?.(attachment.id)}>
                      <X size={13} aria-hidden="true" />
                    </button>
                  </span>
                );
              })}
            </div>
          )}
          <div className="open-science-composer-input-wrap">
            <textarea
              ref={inputRef}
              data-testid="composer-input"
              value={value}
              disabled={disabled}
              onFocus={() => onFocusChange?.(true)}
              onClick={(event) => setCaret(event.currentTarget.selectionStart)}
              onKeyUp={(event) => setCaret(event.currentTarget.selectionStart)}
              onChange={(event) => {
                setCaret(event.target.selectionStart);
                onChange(event.target.value);
              }}
              onPaste={filePaste}
              onKeyDown={onKeyDown}
              placeholder={disabled ? (disabledReason ?? "请先创建或选择项目后开始研究…") : "描述下一项研究任务，或输入 / @ # !…"}
              rows={2}
              aria-label="项目级研究请求"
              aria-expanded={paletteOpen}
              aria-controls={paletteOpen ? "composer-context-palette" : undefined}
            />
            {paletteOpen && (
              <div
                className="open-science-composer-palette"
                data-testid="composer-context-palette"
                id="composer-context-palette"
                role="listbox"
                aria-label={paletteKind === "capabilities" ? "可用请求方式" : activeReferenceTrigger?.kind === "at" ? "项目上下文" : "历史请求"}
              >
                {paletteKind === "capabilities"
                  ? capabilityItems.map((capability, index) => (
                      <button
                        aria-selected={index === activeIndex}
                        className={index === activeIndex ? "is-active" : ""}
                        data-testid={`composer-capability-${capability.id}`}
                        disabled={!capability.available}
                        key={capability.id}
                        role="option"
                        type="button"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => chooseCapability(capability)}
                      >
                        <strong>/{capability.label}</strong>
                        <span>{capability.available ? capability.description : `${capability.description}（当前不可用）`}</span>
                      </button>
                    ))
                  : referenceItems.map((item, index) => (
                      <button
                        aria-selected={index === activeIndex}
                        className={index === activeIndex ? "is-active" : ""}
                        data-testid={`composer-reference-${item.kind}-${item.id}`}
                        key={`${item.kind}:${item.id}`}
                        role="option"
                        type="button"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => chooseReference(item)}
                      >
                        <strong>{item.label}</strong>
                        <span>{item.detail}</span>
                      </button>
                    ))}
              </div>
            )}
          </div>
          <div className="open-science-composer-toolbar">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              onChange={(event) => {
                const files = Array.from(event.target.files ?? []);
                if (files.length) acceptFiles(files);
                event.target.value = "";
              }}
            />
            <button type="button" title="添加附件" aria-label="上传文件" onClick={() => fileInputRef.current?.click()} disabled={uploading || disabled || running}>
              <Paperclip size={16} aria-hidden="true" />
            </button>
            <span>
              {uploading
                ? "正在上传附件…"
                : steerEligible
                  ? "将作为追加指令发送给当前 Lead"
                : running
                  ? "当前请求运行中；输入纯文本后可向当前 Lead 追加指令"
                : controlledCommandUnavailable
                  ? "当前项目没有受控 Runtime/Sandbox；! 不会在本机执行"
                  : controlledCommandNeedsAgent
                    ? "当前有多个执行 Agent；请用 @ 明确选择受授权 Agent"
                    : controlledCommandUnknownAgent
                      ? `找不到执行 Agent @${controlledCommand?.agentId}；请从 @ 列表中选择`
                    : controlledCommandTyped
                    ? "! 仅请求受控 Runtime/Sandbox；仍需真实权限和 Execution"
                    : dragOver
                      ? "松开以上传到当前项目"
                      : "/ 请求方式 · @ 项目文件/Artifact/Agent · # 历史请求 · ! 受控命令"}
            </span>
            {canStop ? (
              <button
                data-testid="composer-stop"
                className="open-science-composer-submit"
                type="button"
                onClick={onStop}
                disabled={stopping}
                aria-label="停止当前研究请求"
                title="停止当前研究请求"
              >
                {stopping ? (
                  <LoaderCircle className="open-science-spinner" size={17} />
                ) : (
                  <Square size={14} fill="currentColor" aria-hidden="true" />
                )}
              </button>
            ) : (
              <button
                data-testid="composer-submit"
                className="open-science-composer-submit"
                type="button"
                onClick={() => void onSubmit()}
                disabled={!canSubmit}
                aria-label={steerEligible ? "向当前 Lead 发送指令" : "发送研究请求"}
                title={steerEligible ? "向当前 Lead 发送指令" : "发送研究请求"}
              >
                {sending ? <LoaderCircle className="open-science-spinner" size={17} /> : <ArrowUp size={18} />}
              </button>
            )}
          </div>
          {dragOver && (
            <div className="open-science-composer-drop-notice" role="status">
              <Upload size={16} aria-hidden="true" />
              <span>松开后将按当前项目的受审计上传流程处理文件。</span>
            </div>
          )}
          {sendError && <p className="open-science-composer-error" role="alert">{sendError}</p>}
          {uploadError && (
            <p className="open-science-composer-error" role="alert">
              <span>{uploadError}</span>
              {onRetryUpload && <button type="button" onClick={onRetryUpload}>重试上传</button>}
            </p>
          )}
        </>
      )}
    </section>
  );
}

function attachmentStatusCopy(inspection?: CadenceAttachment["inspection"]) {
  switch (inspection?.status) {
    case "stored":
      return inspection.reason?.code === "prepare_request_unconfirmed"
        ? "已添加到项目 · 结构化视图状态暂无法确认"
        : "已添加到项目";
    case "preparing":
      return "已添加到项目 · 正在准备结构化视图";
    case "derived_available":
      return "结构化视图已就绪";
    case "unsupported":
    case "unavailable":
      return "已添加到项目 · 暂无结构化视图";
    case "failed":
      return "结构化视图未生成；原文件仍在项目中";
    case "integrity_mismatch":
      return "结构化视图未通过校验；原文件仍在项目中";
    default:
      return undefined;
  }
}

function visualObservationStatusCopy(inspection?: CadenceAttachment["inspection"]) {
  const coverage = inspection?.coverage;
  if (!coverage || typeof coverage.visual !== "object" || coverage.visual === null) return undefined;
  const visual = coverage.visual as Record<string, unknown>;
  const status = typeof visual.status === "string" ? visual.status : undefined;
  if (!status) return undefined;
  const model =
    typeof visual.model === "object" && visual.model !== null
      ? (visual.model as Record<string, unknown>)
      : undefined;
  const modelId = typeof model?.id === "string" && model.id.trim() ? model.id.trim() : undefined;
  const modelRevision =
    typeof model?.revision === "string" && model.revision.trim()
      ? model.revision.trim()
      : undefined;
  // The observation is deliberately non-evidentiary, but its fixed local
  // model identity must remain visible so a researcher can distinguish it
  // from Docling OCR/layout and audit the exact admitted observation path.
  const modelLabel = [modelId, modelRevision]
    .filter((value): value is string => Boolean(value))
    .map((value) => ` · ${value}`)
    .join("");
  switch (status) {
    case "available":
      return `本地视觉观察已就绪（定性、非证据）${modelLabel}`;
    case "not_attempted":
      return `本地视觉观察未运行；OCR/layout 仍可用${modelLabel}`;
    case "unavailable":
    case "failed":
      return `本地视觉观察不可用；OCR/layout 仍可用${modelLabel}`;
    default:
      return undefined;
  }
}

function documentSelectionStatusCopy(selection: NonNullable<CadenceAttachment["documentSelection"]>) {
  const pages = [
    ...new Set(
      selection.locators.flatMap((locator) =>
        locator.page === undefined ? [] : [locator.page],
      ),
    ),
  ];
  const pageSummary = pages.length ? ` · 第 ${pages.join("、")} 页` : "";
  return `已附带 ${selection.locators.length} 个元素${pageSummary}`;
}
